/**
 * Mock implementation of the `@clerk/clerk-react` surface — swapped in
 * via a Vite resolve.alias when `VITE_E2E_MODE=true`.
 *
 * Purpose: Playwright tests need to render the workspace WITHOUT a
 * real Clerk session. This module reproduces the exact public surface
 * the codebase imports (ClerkProvider / SignedIn / SignedOut / SignIn /
 * SignUp / UserButton / useAuth / useUser) so a single alias flip
 * replaces real Clerk everywhere without per-file rewrites.
 *
 *   import { useAuth } from "@clerk/clerk-react";   ← in app code
 *           ↓ vite.config.ts alias when VITE_E2E_MODE=true
 *   resolves to this file
 *
 * Identity returned: a fixed "E2E HR Director" — userId/org/role are
 * stable across runs so screenshot diffs only reflect real UI changes.
 *
 * SAFETY: this file ships in the bundle ONLY when VITE_E2E_MODE is set.
 * The Vite config asserts `mode !== "production"` when the flag is on,
 * so a production build with the flag set FAILS at build time
 * (see console/vite.config.ts).
 *
 * To extend: when a future PR uses a new Clerk hook/component, add it
 * here. Anything not stubbed will error at render time, which is the
 * desired loud-failure behavior.
 */

import { createContext, type ReactNode, useContext, useMemo } from "react";

// ---------------------------------------------------------------------------
// Fixed identity — stable across every E2E run
// ---------------------------------------------------------------------------

const E2E_IDENTITY = {
  userId: "user_e2e_hr_director",
  orgId: "org_e2e_riyadh",
  role: "hr_director",
  email: "hr.director@e2e.solvait.test",
  firstName: "E2E",
  lastName: "Director",
} as const;

// E2E tests can override via window.__SOLVAIT_E2E_IDENTITY__ before
// the React tree mounts (set in Playwright's page.addInitScript). The
// default is the HR Director above; overrides let role-gated UI be
// tested per-spec.
function readOverride(): Partial<typeof E2E_IDENTITY> {
  if (typeof window === "undefined") return {};
  const win = window as unknown as {
    __SOLVAIT_E2E_IDENTITY__?: Partial<typeof E2E_IDENTITY>;
  };
  return win.__SOLVAIT_E2E_IDENTITY__ ?? {};
}

function currentIdentity(): typeof E2E_IDENTITY {
  return { ...E2E_IDENTITY, ...readOverride() };
}

// ---------------------------------------------------------------------------
// React context — used by useAuth / useUser
// ---------------------------------------------------------------------------

interface MockClerkContextValue {
  isSignedIn: boolean;
  isLoaded: boolean;
}

const MockClerkContext = createContext<MockClerkContextValue>({
  isSignedIn: true,
  isLoaded: true,
});

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

interface ClerkProviderProps {
  children: ReactNode;
  publishableKey?: string;
  /** Optional Clerk appearance overrides — ignored in the mock. */
  appearance?: unknown;
}

export function ClerkProvider({
  children,
}: ClerkProviderProps): ReactNode {
  const value = useMemo<MockClerkContextValue>(
    () => ({ isSignedIn: true, isLoaded: true }),
    [],
  );
  return (
    <MockClerkContext.Provider value={value}>
      {children}
    </MockClerkContext.Provider>
  );
}

/** SignedIn renders children unconditionally — the mock is "signed in". */
export function SignedIn({ children }: { children: ReactNode }): ReactNode {
  return <>{children}</>;
}

/** SignedOut renders nothing — the mock is never signed out. */
export function SignedOut(_props: { children: ReactNode }): ReactNode {
  return null;
}

/**
 * SignIn / SignUp would normally render Clerk's hosted forms. In E2E
 * mode they're unreachable (SignedOut never renders), but stub them
 * so the codebase imports don't break.
 */
export function SignIn(_props: Record<string, unknown>): ReactNode {
  return (
    <div data-testid="mock-clerk-sign-in">[mock] SignIn placeholder</div>
  );
}

export function SignUp(_props: Record<string, unknown>): ReactNode {
  return (
    <div data-testid="mock-clerk-sign-up">[mock] SignUp placeholder</div>
  );
}

/**
 * UserButton in real Clerk is the avatar + menu in the top bar. In the
 * mock it's a static button with the E2E identity initials so
 * screenshots are deterministic.
 */
export function UserButton(_props: Record<string, unknown>): ReactNode {
  const { firstName, lastName } = currentIdentity();
  const initials =
    (firstName?.[0] ?? "") + (lastName?.[0] ?? "") || "EU";
  return (
    <button
      type="button"
      data-testid="mock-clerk-user-button"
      aria-label="User menu (mock)"
      className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-deep text-xs font-semibold text-white"
    >
      {initials}
    </button>
  );
}

/**
 * Real Clerk exposes `UserButton.MenuItems` + `UserButton.Link` as
 * compound-component slots for injecting custom menu links. The mock
 * accepts them as no-op render slots so JSX like
 *   <UserButton><UserButton.MenuItems>...</UserButton.MenuItems></UserButton>
 * resolves at module-eval time without breaking the test bundle.
 */
UserButton.MenuItems = function MenuItems(
  _props: { children?: ReactNode },
): ReactNode {
  return null;
};
UserButton.Link = function Link(
  _props: { label?: string; labelIcon?: ReactNode; href?: string },
): ReactNode {
  return null;
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

interface MockUser {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  primaryEmailAddress: { emailAddress: string };
  publicMetadata: { role: string };
}

interface MockUseAuthReturn {
  isLoaded: boolean;
  isSignedIn: boolean;
  userId: string;
  orgId: string | null;
  sessionId: string;
  /** Real Clerk's getToken is async; we mimic the signature. The token
   *  is a sentinel string the backend treats as the e2e identity. */
  getToken: (options?: { template?: string }) => Promise<string | null>;
  signOut: () => Promise<void>;
}

interface MockUseUserReturn {
  isLoaded: boolean;
  isSignedIn: boolean;
  user: MockUser | null;
}

export function useAuth(): MockUseAuthReturn {
  // Read context purely for the contract — mock doesn't change
  // values per consumer.
  useContext(MockClerkContext);
  const id = currentIdentity();
  return {
    isLoaded: true,
    isSignedIn: true,
    userId: id.userId,
    orgId: id.orgId,
    sessionId: "session_e2e",
    getToken: async () => "e2e-mock-token",
    signOut: async () => {
      /* no-op in mock */
    },
  };
}

export function useUser(): MockUseUserReturn {
  useContext(MockClerkContext);
  const id = currentIdentity();
  const user: MockUser = {
    id: id.userId,
    firstName: id.firstName,
    lastName: id.lastName,
    fullName: `${id.firstName} ${id.lastName}`,
    primaryEmailAddress: { emailAddress: id.email },
    publicMetadata: { role: id.role },
  };
  return { isLoaded: true, isSignedIn: true, user };
}

/**
 * Stub of real Clerk's `useClerk` hook. The OrgAutoSwitch component
 * pulls `clerk.setActive(...)` to flip active org on tenant subdomains;
 * in the mock there's no real Clerk session and the upstream guards
 * (`!user.organizationMemberships`) short-circuit before `setActive`
 * ever fires, so the stub is no-op. Anything new the codebase calls on
 * the Clerk instance should be added here.
 */
interface MockClerk {
  setActive: (opts: { organization?: string }) => Promise<void>;
}

export function useClerk(): MockClerk {
  useContext(MockClerkContext);
  return {
    setActive: async () => {
      /* no-op in mock — real Clerk swaps active org; mock has only one */
    },
  };
}

// ---------------------------------------------------------------------------
// Type stub — some imports use the Clerk namespace types
// ---------------------------------------------------------------------------

export type {
  ClerkProviderProps,
  MockClerkContextValue as ClerkContextValue,
};
